export const OWNER_CONSOLE_BASE = '/sole-con-mana-ment-ge'

export type OwnerRouteQuery = {
  filter?: string
  q?: string
  bg?: string
  for?: string
  imp?: string
  view?: string
  [key: string]: string | undefined
}

export type OwnerRoute =
  | { view: 'home'; query?: OwnerRouteQuery }
  | { view: 'workspaces'; query?: OwnerRouteQuery }
  | { view: 'workspace-detail'; workspaceId: string; query?: OwnerRouteQuery }
  | { view: 'people'; query?: OwnerRouteQuery }
  | { view: 'person-detail'; userId: string; query?: OwnerRouteQuery }
  | { view: 'inbox'; query?: OwnerRouteQuery }
  | { view: 'ticket-detail'; ticketId: string; query?: OwnerRouteQuery }
  | { view: 'feedback'; query?: OwnerRouteQuery }
  | { view: 'feedback-detail'; surveyId: string; query?: OwnerRouteQuery }
  | { view: 'broadcast'; query?: OwnerRouteQuery }
  | { view: 'broadcast-new'; query?: OwnerRouteQuery }
  | { view: 'broadcast-detail'; broadcastId: string; query?: OwnerRouteQuery }
  | { view: 'ops'; query?: OwnerRouteQuery }
  | { view: 'incident-detail'; incidentId: string; query?: OwnerRouteQuery }
  | { view: 'logs'; query?: OwnerRouteQuery }
  | { view: 'logs-detail'; logId: string; query?: OwnerRouteQuery }
  | { view: 'analytics'; query?: OwnerRouteQuery }
  | { view: 'revenue'; query?: OwnerRouteQuery }
  | { view: 'costs'; query?: OwnerRouteQuery }
  | { view: 'config-settings'; query?: OwnerRouteQuery }
  | { view: 'config-flags'; query?: OwnerRouteQuery }
  | { view: 'config-flag-detail'; flagKey: string; query?: OwnerRouteQuery }
  | { view: 'config-cohorts'; query?: OwnerRouteQuery }
  | { view: 'config-cohort-detail'; cohortId: string; query?: OwnerRouteQuery }
  | { view: 'config-economy'; query?: OwnerRouteQuery }
  | { view: 'config-plan-limits'; query?: OwnerRouteQuery }
  | { view: 'config-templates'; query?: OwnerRouteQuery }
  | { view: 'config-template-detail'; templateId: string; query?: OwnerRouteQuery }
  | { view: 'config-feedback-authoring'; query?: OwnerRouteQuery }
  | { view: 'config-feedback-authoring-detail'; surveyId: string; query?: OwnerRouteQuery }
  | { view: 'config-experience-engine'; query?: OwnerRouteQuery }
  | { view: 'config-moderation'; query?: OwnerRouteQuery }
  | { view: 'config-actions'; query?: OwnerRouteQuery }
  | { view: 'config-integrations'; query?: OwnerRouteQuery }
  | { view: 'config-line'; query?: OwnerRouteQuery }
  | { view: 'config-playground'; query?: OwnerRouteQuery }
  | { view: 'config-demo-lane'; query?: OwnerRouteQuery }
  | { view: 'config-client-outreach'; query?: OwnerRouteQuery }
  | { view: 'activity'; query?: OwnerRouteQuery }
  | { view: 'activity-detail'; actionId: string; query?: OwnerRouteQuery }
  | { view: 'support-access'; query?: OwnerRouteQuery }

export const parseQueryParams = (search: string): OwnerRouteQuery | undefined => {
  const cleanSearch = search.startsWith('?') ? search.slice(1) : search
  if (!cleanSearch) return undefined
  const params = new URLSearchParams(cleanSearch)
  const query: OwnerRouteQuery = {}
  params.forEach((val, key) => {
    query[key] = val
  })
  return Object.keys(query).length > 0 ? query : undefined
}

export const serializeQueryParams = (query?: OwnerRouteQuery): string => {
  if (!query) return ''
  const entries = Object.entries(query).filter(
    (entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '',
  )
  if (entries.length === 0) return ''
  const searchParams = new URLSearchParams()
  entries.forEach(([key, val]) => searchParams.append(key, val))
  const qs = searchParams.toString()
  return qs ? `?${qs}` : ''
}

export const parseOwnerRoute = (rawSubPath: string = '', rawSearch: string = ''): OwnerRoute => {
  let subPath = rawSubPath.trim()
  let search = rawSearch.trim()

  if (subPath.startsWith(OWNER_CONSOLE_BASE)) {
    subPath = subPath.slice(OWNER_CONSOLE_BASE.length)
  }

  if (subPath.includes('?')) {
    const [pathPart, queryPart] = subPath.split('?', 2)
    subPath = pathPart
    if (!search && queryPart) {
      search = `?${queryPart}`
    }
  }

  let query = parseQueryParams(search)
  // Groups was a duplicate workspace surface. Preserve old deep links by
  // resolving them as the canonical list rather than keeping a dead branch.
  if (query?.view === 'groups') {
    delete query.view
    if (Object.keys(query).length === 0) query = undefined
  }

  // Normalize: ensure leading slash, strip trailing slash except for root
  if (!subPath.startsWith('/')) {
    subPath = `/${subPath}`
  }
  if (subPath.length > 1 && subPath.endsWith('/')) {
    subPath = subPath.slice(0, -1)
  }

  const segments = subPath.slice(1).split('/').filter(Boolean)

  if (segments.length === 0) {
    return { view: 'home', query }
  }

  const [s0, s1, s2] = segments

  if (s0 === 'workspaces') {
    if (s1) return { view: 'workspace-detail', workspaceId: decodeURIComponent(s1), query }
    return { view: 'workspaces', query }
  }

  if (s0 === 'people') {
    if (s1) return { view: 'person-detail', userId: decodeURIComponent(s1), query }
    return { view: 'people', query }
  }

  if (s0 === 'inbox') {
    if (s1) return { view: 'ticket-detail', ticketId: decodeURIComponent(s1), query }
    return { view: 'inbox', query }
  }

  if (s0 === 'feedback') {
    if (s1) return { view: 'feedback-detail', surveyId: decodeURIComponent(s1), query }
    return { view: 'feedback', query }
  }

  if (s0 === 'broadcast') {
    if (s1 === 'new') return { view: 'broadcast-new', query }
    if (s1) return { view: 'broadcast-detail', broadcastId: decodeURIComponent(s1), query }
    return { view: 'broadcast', query }
  }

  if (s0 === 'ops') {
    if (s1 === 'incidents' && s2) {
      return { view: 'incident-detail', incidentId: decodeURIComponent(s2), query }
    }
    return { view: 'ops', query }
  }

  if (s0 === 'logs') {
    if (s1) return { view: 'logs-detail', logId: decodeURIComponent(s1), query }
    return { view: 'logs', query }
  }

  if (s0 === 'analytics') {
    return { view: 'analytics', query }
  }

  if (s0 === 'revenue') {
    return { view: 'revenue', query }
  }

  if (s0 === 'costs') {
    return { view: 'costs', query }
  }

  if (s0 === 'activity') {
    if (s1) return { view: 'activity-detail', actionId: decodeURIComponent(s1), query }
    return { view: 'activity', query }
  }

  if (s0 === 'support-access') {
    return { view: 'support-access', query }
  }

  if (s0 === 'config') {
    if (s1 === 'settings') return { view: 'config-settings', query }
    if (s1 === 'flags') {
      if (s2) return { view: 'config-flag-detail', flagKey: decodeURIComponent(s2), query }
      return { view: 'config-flags', query }
    }
    if (s1 === 'cohorts') {
      if (s2) return { view: 'config-cohort-detail', cohortId: decodeURIComponent(s2), query }
      return { view: 'config-cohorts', query }
    }
    if (s1 === 'economy') return { view: 'config-economy', query }
    if (s1 === 'plan-limits') return { view: 'config-plan-limits', query }
    if (s1 === 'templates') {
      if (s2) return { view: 'config-template-detail', templateId: decodeURIComponent(s2), query }
      return { view: 'config-templates', query }
    }
    if (s1 === 'feedback-authoring') {
      if (s2) return { view: 'config-feedback-authoring-detail', surveyId: decodeURIComponent(s2), query }
      return { view: 'config-feedback-authoring', query }
    }
    if (s1 === 'experience-engine') return { view: 'config-experience-engine', query }
    if (s1 === 'moderation') return { view: 'config-moderation', query }
    if (s1 === 'actions') return { view: 'config-actions', query }
    if (s1 === 'integrations') return { view: 'config-integrations', query }
    if (s1 === 'line') return { view: 'config-line', query }
    if (s1 === 'playground') return { view: 'config-playground', query }
    if (s1 === 'demo-lane') return { view: 'config-demo-lane', query }
    if (s1 === 'client-outreach') return { view: 'config-client-outreach', query }
  }

  // Unknown paths resolve to the Home route, never to a crash
  return { view: 'home', query }
}

export const ownerRouteToPath = (route: OwnerRoute): string => {
  const qs = serializeQueryParams(route.query)
  let sub = ''

  switch (route.view) {
    case 'home':
      sub = ''
      break
    case 'workspaces':
      sub = '/workspaces'
      break
    case 'workspace-detail':
      sub = `/workspaces/${encodeURIComponent(route.workspaceId)}`
      break
    case 'people':
      sub = '/people'
      break
    case 'person-detail':
      sub = `/people/${encodeURIComponent(route.userId)}`
      break
    case 'inbox':
      sub = '/inbox'
      break
    case 'ticket-detail':
      sub = `/inbox/${encodeURIComponent(route.ticketId)}`
      break
    case 'feedback':
      sub = '/feedback'
      break
    case 'feedback-detail':
      sub = `/feedback/${encodeURIComponent(route.surveyId)}`
      break
    case 'broadcast':
      sub = '/broadcast'
      break
    case 'broadcast-new':
      sub = '/broadcast/new'
      break
    case 'broadcast-detail':
      sub = `/broadcast/${encodeURIComponent(route.broadcastId)}`
      break
    case 'ops':
      sub = '/ops'
      break
    case 'incident-detail':
      sub = `/ops/incidents/${encodeURIComponent(route.incidentId)}`
      break
    case 'logs':
      sub = '/logs'
      break
    case 'logs-detail':
      sub = `/logs/${encodeURIComponent(route.logId)}`
      break
    case 'analytics':
      sub = '/analytics'
      break
    case 'revenue':
      sub = '/revenue'
      break
    case 'costs':
      sub = '/costs'
      break
    case 'config-settings':
      sub = '/config/settings'
      break
    case 'config-flags':
      sub = '/config/flags'
      break
    case 'config-flag-detail':
      sub = `/config/flags/${encodeURIComponent(route.flagKey)}`
      break
    case 'config-cohorts':
      sub = '/config/cohorts'
      break
    case 'config-cohort-detail':
      sub = `/config/cohorts/${encodeURIComponent(route.cohortId)}`
      break
    case 'config-economy':
      sub = '/config/economy'
      break
    case 'config-plan-limits':
      sub = '/config/plan-limits'
      break
    case 'config-templates':
      sub = '/config/templates'
      break
    case 'config-template-detail':
      sub = `/config/templates/${encodeURIComponent(route.templateId)}`
      break
    case 'config-feedback-authoring':
      sub = '/config/feedback-authoring'
      break
    case 'config-feedback-authoring-detail':
      sub = `/config/feedback-authoring/${encodeURIComponent(route.surveyId)}`
      break
    case 'config-experience-engine':
      sub = '/config/experience-engine'
      break
    case 'config-moderation':
      sub = '/config/moderation'
      break
    case 'config-actions':
      sub = '/config/actions'
      break
    case 'config-integrations':
      sub = '/config/integrations'
      break
    case 'config-line':
      sub = '/config/line'
      break
    case 'config-playground':
      sub = '/config/playground'
      break
    case 'config-demo-lane':
      sub = '/config/demo-lane'
      break
    case 'config-client-outreach':
      sub = '/config/client-outreach'
      break
    case 'activity':
      sub = '/activity'
      break
    case 'activity-detail':
      sub = `/activity/${encodeURIComponent(route.actionId)}`
      break
    case 'support-access':
      sub = '/support-access'
      break
  }

  const base = sub ? `${OWNER_CONSOLE_BASE}${sub}` : OWNER_CONSOLE_BASE
  return `${base}${qs}`
}

export const navigateOwner = (target: OwnerRoute | string): void => {
  if (typeof window === 'undefined') return
  const path = typeof target === 'string'
    ? (target.startsWith(OWNER_CONSOLE_BASE) ? target : `${OWNER_CONSOLE_BASE}${target.startsWith('/') ? '' : '/'}${target}`)
    : ownerRouteToPath(target)

  if (window.location.pathname + window.location.search === path) {
    return
  }

  window.history.pushState(null, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
}
